import { QuoteAddress, QuoteRequirementSection } from './types/basket-types';
import { AddressCustomsFields, OrderCustomsFields } from './types/customs';

export function validateForm(form: HTMLFormElement) {
  const e = {} as Record<string, string>;
  let i = 0;
  for (const input of form.querySelectorAll('input')) {
    const forceErrorAttr = input.getAttribute('data-force-error-message');
    if (forceErrorAttr) {
      e[input.id || input.name] = forceErrorAttr;
      if (i === 0) {
        if (input.type === 'hidden') {
          input.parentElement?.scrollIntoView();
        } else {
          input.focus();
        }
      }
      i++;
    } else if (input.checkValidity && !input.checkValidity()) {
      e[input.id || input.name] = input.validationMessage;
      if (i === 0) {
        if (input.type === 'checkbox') {
          input.scrollIntoView();
        } else {
          input.focus();
        }
      }
      i++;
    } else if (
      ['text', 'tel', 'email'].some((x) => x === input.type) &&
      input.maxLength &&
      input.maxLength > 0 &&
      input.value.length > input.maxLength
    ) {
      input.setCustomValidity(`Please enter no more than ${input.maxLength} characters`);
      e[input.id || input.name] = input.validationMessage;
      if (i === 0) {
        if (input.type === 'checkbox') {
          input.scrollIntoView();
        } else {
          input.focus();
        }
      }
      i++;
    } else if (
      ['text', 'tel', 'email'].some((x) => x === input.type) &&
      input.minLength &&
      input.minLength > 0 &&
      input.value.length < input.minLength
    ) {
      input.setCustomValidity(`Please enter at least ${input.minLength} characters`);
      e[input.id || input.name] = input.validationMessage;
      if (i === 0) {
        if (input.type === 'checkbox') {
          input.scrollIntoView();
        } else {
          input.focus();
        }
      }
      i++;
    } else {
      if (input.dataset.customValError) {
        input.setCustomValidity(input.dataset.customValError ?? 'Error fallback message (needs translating)');
        e[input.id || input.name] = input.validationMessage;
      }
    }
  }
  return e;
}

export function getInvalidCount(id?: string) {
  const output = { count: 0, error: 0, percentageComplete: 100 };

  const form = (id && document.getElementById(id)) || document.querySelector('form')?.[0] || null;
  if (!form) {
    return output;
  }

  const inputs = form?.querySelectorAll('input') || [];
  output.count = inputs.length;

  if (output.count === 0) {
    return output;
  }

  for (const input of inputs) {
    if ((input.checkValidity && !input.checkValidity()) || input.getAttribute('data-force-error-message')) {
      output.error++;
    }
  }

  return { ...output, percentageComplete: Math.floor(((output.count - output.error) / output.count) * 100) };
}

export function validateAddress(address: QuoteAddress, requirements: QuoteRequirementSection) {
  const overview = ['contactname', 'email', 'phone'];
  for (const rule of (requirements.rules || []).filter(
    (r) => !AddressCustomsFields.includes(r.key) && !OrderCustomsFields.includes(r.key)
  )) {
    if (overview.includes(rule.key)) {
      continue;
    }
    const value = address[rule.key] as string | undefined;
    if (rule.required && (!value || value === '')) {
      return false;
    }
    if (rule.metadata.minLength && value && value.length < rule.metadata.minLength) {
      return false;
    }
    if (rule.metadata.maxLength && value && value.length > rule.metadata.maxLength) {
      return false;
    }
    if (rule.metadata.regex && value && !new RegExp(rule.metadata.regex).test(value)) {
      return false;
    }
  }

  return true;
}

export function cleanAddressFields(address: QuoteAddress, requirements: QuoteRequirementSection) {
  if (address.postcode && !requirements.rules.find((r) => r.key === 'postcode')) {
    delete address.postcode;
  }

  if (address.county && !requirements.rules.find((r) => r.key === 'county')) {
    delete address.county;
  }

  if (address.locality && !requirements.rules.find((r) => r.key === 'locality')) {
    delete address.locality;
  }

  if (address.town && !requirements.rules.find((r) => r.key === 'town')) {
    delete address.town;
  }

  if (address.street && !requirements.rules.find((r) => r.key === 'street')) {
    delete address.street;
  }

  if (address.property && !requirements.rules.find((r) => r.key === 'property')) {
    delete address.property;
  }

  if (address.organisation && !requirements.rules.find((r) => r.key === 'organisation')) {
    delete address.organisation;
  }

  return address;
}
